/**
 * Course Wizard Component
 * A step-by-step wizard for creating and editing courses
 * Steps: 1. Course Info -> 2. Modules -> 3. Quiz -> 4. Assignment (Optional)
 */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { useTenant } from '../../contexts/TenantContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Separator } from '../ui/separator';
import { Switch } from '../ui/switch';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import {
  BookOpen,
  Video,
  FileText,
  Link as LinkIcon,
  Upload,
  Plus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Loader2,
  Brain,
  Sparkles,
  Users,
  CalendarIcon,
  GripVertical,
  X,
  Check,
  ChevronsUpDown,
  Clock,
  Award,
  SkipForward,
  AlertCircle,
  ShieldAlert,
  Image,
  FileQuestion,
  FileSpreadsheet,
  Headphones,
  FileType,
  Presentation,
} from 'lucide-react';
import { format, isBefore, startOfDay } from 'date-fns';
import { cn } from '../../lib/utils';
import { moderateUrl, moderateImage, isSuspiciousUrl } from '../../lib/contentModeration';
import { SlideEditor } from './SlideEditor';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'Sales Training',
  'Product Knowledge',
  'Customer Service',
  'Compliance',
  'Leadership',
  'Soft Skills',
  'Technical',
  'Onboarding',
  'Safety',
  'Other',
];

const DIFFICULTIES = ['Beginner', 'Intermediate', 'Advanced'];

const QUESTION_TYPES = [
  { value: 'single_choice', label: 'Single Choice' },
  { value: 'multiple_choice', label: 'Multiple Choice' },
  { value: 'true_false', label: 'True/False' },
  { value: 'match_following', label: 'Match the Following' },
  { value: 'image_based', label: 'Image-Based' },
  { value: 'scenario', label: 'Scenario-Based' },
];

// Validation helpers
const validateTitle = (title, minLen = 10, maxLen = 50) => {
  const errors = [];
  const trimmed = title.trim();
  
  if (trimmed.length < minLen) {
    errors.push(`Minimum ${minLen} characters required (${trimmed.length}/${minLen})`);
  }
  if (trimmed.length > maxLen) {
    errors.push(`Maximum ${maxLen} characters allowed (${trimmed.length}/${maxLen})`);
  }
  // Check if only numbers
  if (/^\d+$/.test(trimmed)) {
    errors.push('Title cannot be only numbers');
  }
  // Check if all same character repeated
  if (trimmed.length > 3 && /^(.)\1+$/.test(trimmed)) {
    errors.push('Title cannot be all repeated characters');
  }
  // Check if mostly repeated characters (80%+ same char)
  if (trimmed.length > 5) {
    const charCount = {};
    for (const char of trimmed.toLowerCase()) {
      charCount[char] = (charCount[char] || 0) + 1;
    }
    const maxCount = Math.max(...Object.values(charCount));
    if (maxCount / trimmed.length > 0.8) {
      errors.push('Title has too many repeated characters');
    }
  }
  
  return { isValid: errors.length === 0, errors };
};

const isValidVideoUrl = (url) => {
  if (!url) return false;
  const videoPatterns = [
    /youtube\.com\/watch/i,
    /youtu\.be\//i,
    /vimeo\.com/i,
    /\.mp4$/i,
    /\.webm$/i,
    /\.mov$/i,
    /\.avi$/i,
    /\.mkv$/i,
    /\/api\/uploads\/.*video/i,
  ];
  return videoPatterns.some(pattern => pattern.test(url));
};

const VALID_VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.m4v', '.wmv'];

const isValidVideoFile = (file) => {
  if (!file) return false;
  const ext = '.' + file.name.split('.').pop().toLowerCase();
  return VALID_VIDEO_EXTENSIONS.includes(ext) || file.type.startsWith('video/');
};

export const CourseWizard = ({
  open,
  onOpenChange,
  editingCourse = null,
  onSaveComplete,
  users = [],
}) => {
  const { t } = useTranslation();
  const { hasFeature, isPlatformUser, edition } = useTenant();
  
  // Create translated quiz type options
  const QUIZ_TYPE_OPTIONS = useMemo(() => [
    { value: 'pre_test', label: t('courses.quizTypes.preTest', 'Pre Test'), description: t('courses.quizTypes.preTestDesc', 'Before starting modules') },
    { value: 'in_between', label: t('courses.quizTypes.inBetween', 'In Between Modules'), description: t('courses.quizTypes.inBetweenDesc', 'Quiz after specific modules') },
    { value: 'post_test', label: t('courses.quizTypes.postTest', 'Post Test'), description: t('courses.quizTypes.postTestDesc', 'Final assessment') },
  ], [t]);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [courseId, setCourseId] = useState(editingCourse?.id || null);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false); // Close confirmation dialog
  
  // Check if AI Quiz Generation is available
  const canUseAIQuizGen = isPlatformUser || hasFeature('ai_features') || edition !== 'standard';
  
  // Step 1: Course Info
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    category: '',
    difficulty: 'Beginner',
    duration_minutes: 30,
    points_reward: 100,
    thumbnail_url: '',
    is_published: false,
  });

  // Step 2: Modules
  const [modules, setModules] = useState([]);
  const [moduleForm, setModuleForm] = useState({
    title: '',
    type: 'video',
    content: '',
    video_url: '',
    videoFile: null,
  });
  const [editingModuleIndex, setEditingModuleIndex] = useState(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const videoInputRef = useRef(null);
  
  // Module Type Modal
  const [showModuleTypeModal, setShowModuleTypeModal] = useState(false);
  const [showModuleFormModal, setShowModuleFormModal] = useState(false);
  const [slideEditorOpen, setSlideEditorOpen] = useState(false);
  
  // Thumbnail upload
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const thumbnailInputRef = useRef(null);

  // Validation state
  const [courseTitleErrors, setCourseTitleErrors] = useState([]);
  const [moduleTitleErrors, setModuleTitleErrors] = useState([]);
  const [moduleContentError, setModuleContentError] = useState('');

  // Step 3: Quiz
  const [quizEnabled, setQuizEnabled] = useState(false);
  const [selectedQuizTypes, setSelectedQuizTypes] = useState([]); // Array of: 'pre_test', 'in_between', 'post_test'
  const [activeQuizType, setActiveQuizType] = useState(null); // Currently editing quiz type
  const [selectedModuleForQuiz, setSelectedModuleForQuiz] = useState(null); // For in_between quizzes - which module
  const [quizForms, setQuizForms] = useState({
    pre_test: {
      title: 'Pre-Test',
      passing_score: 70,
      time_limit: 30,
      max_attempts: 3,
      show_correct_answers: true,
      shuffle_questions: false,
      shuffle_options: false,
      questions: [],
    },
    in_between: {
      // Store quizzes by module index: { 0: {...quiz}, 1: {...quiz} }
      module_quizzes: {},
    },
    post_test: {
      title: 'Post-Test',
      passing_score: 70,
      time_limit: 30,
      max_attempts: 3,
      show_correct_answers: true,
      shuffle_questions: false,
      shuffle_options: false,
      questions: [],
    },
  });
  // Legacy quizForm for backward compatibility
  const [quizForm, setQuizForm] = useState({
    title: '',
    passing_score: 70,
    time_limit: 30,
    max_attempts: 3,
    show_correct_answers: true,
    shuffle_questions: false,
    shuffle_options: false,
    questions: [],
  });
  const [generatingQuiz, setGeneratingQuiz] = useState(false);

  // Initialize module quiz form
  const getDefaultModuleQuiz = (moduleIndex) => ({
    title: `Module ${moduleIndex + 1} Quiz`,
    passing_score: 70,
    time_limit: 15,
    max_attempts: 3,
    show_correct_answers: true,
    shuffle_questions: false,
    shuffle_options: false,
    questions: [],
    after_module_index: moduleIndex,
  });

  // Helper to get current quiz form based on active type
  const getCurrentQuizForm = () => {
    if (activeQuizType === 'in_between' && selectedModuleForQuiz !== null) {
      const moduleQuiz = quizForms.in_between.module_quizzes[selectedModuleForQuiz];
      return moduleQuiz || getDefaultModuleQuiz(selectedModuleForQuiz);
    }
    if (activeQuizType && quizForms[activeQuizType]) {
      return quizForms[activeQuizType];
    }
    return quizForm;
  };

  // Helper to update current quiz form
  const updateCurrentQuizForm = (updates) => {
    if (activeQuizType === 'in_between' && selectedModuleForQuiz !== null) {
      setQuizForms(prev => ({
        ...prev,
        in_between: {
          ...prev.in_between,
          module_quizzes: {
            ...prev.in_between.module_quizzes,
            [selectedModuleForQuiz]: {
              ...(prev.in_between.module_quizzes[selectedModuleForQuiz] || getDefaultModuleQuiz(selectedModuleForQuiz)),
              ...updates
            }
          }
        }
      }));
    } else if (activeQuizType && quizForms[activeQuizType]) {
      setQuizForms(prev => ({
        ...prev,
        [activeQuizType]: { ...prev[activeQuizType], ...updates }
      }));
    } else {
      setQuizForm(prev => ({ ...prev, ...updates }));
    }
  };

  // Get total questions count for in_between quizzes
  const getInBetweenQuestionsCount = () => {
    const moduleQuizzes = quizForms.in_between.module_quizzes || {};
    return Object.values(moduleQuizzes).reduce((total, quiz) => total + (quiz.questions?.length || 0), 0);
  };

  // Get configured modules for in_between
  const getConfiguredModulesCount = () => {
    const moduleQuizzes = quizForms.in_between.module_quizzes || {};
    return Object.values(moduleQuizzes).filter(q => q.questions?.length > 0).length;
  };

  // Content Moderation
  const [contentWarning, setContentWarning] = useState(null);
  const [checkingContent, setCheckingContent] = useState(false);
  
  // Document/Audio upload states
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const documentInputRef = useRef(null);
  const audioInputRef = useRef(null);

  // Step 4: Assignment
  const [assignmentForm, setAssignmentForm] = useState({
    roles: [],
    due_date: null,
  });
  const [roleSelectOpen, setRoleSelectOpen] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);

  // Fetch roles from backend (same as User Management)
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/roles/available`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data && response.data.length > 0) {
          setAvailableRoles(response.data.map(r => ({
            value: r.value,
            label: r.label,
          })));
        }
      } catch (error) {
        // Fallback to default roles if API fails
        setAvailableRoles([
          { value: 'cra', label: 'CRA' },
          { value: 'store_manager', label: 'Store Manager' },
          { value: 'regional_manager', label: 'Regional Manager' },
          { value: 'training_team', label: 'Training Team' },
          { value: 'admin', label: 'Administrator' },
        ]);
      }
    };
    if (open) {
      fetchRoles();
    }
  }, [open]);

  // Initialize form when editing
  useEffect(() => {
    if (editingCourse) {
      setCourseId(editingCourse.id);
      setCourseForm({
        title: editingCourse.title || '',
        description: editingCourse.description || '',
        category: editingCourse.category || '',
        difficulty: editingCourse.difficulty || 'Beginner',
        duration_minutes: editingCourse.duration_minutes || 30,
        points_reward: editingCourse.points_reward || 100,
        thumbnail_url: editingCourse.thumbnail_url || '',
        is_published: editingCourse.is_published || false,
      });
      setModules(editingCourse.modules || []);
      
      // Load new multi-quiz structure if present
      if (editingCourse.quizzes) {
        setQuizEnabled(true);
        const typesToSelect = [];
        
        // Load Pre-test
        if (editingCourse.quizzes.pre_test && editingCourse.quizzes.pre_test.questions?.length > 0) {
          typesToSelect.push('pre_test');
        }
        
        // Load Post-test
        if (editingCourse.quizzes.post_test && editingCourse.quizzes.post_test.questions?.length > 0) {
          typesToSelect.push('post_test');
        }
        
        // Load In-between quizzes
        if (editingCourse.quizzes.in_between?.module_quizzes) {
          const hasInBetween = Object.values(editingCourse.quizzes.in_between.module_quizzes).some(q => q?.questions?.length > 0);
          if (hasInBetween) {
            typesToSelect.push('in_between');
          }
        }
        
        setSelectedQuizTypes(typesToSelect);
        
        // Helper function to ensure quiz form has all required fields with defaults
        const normalizeQuizForm = (form, defaults) => {
          if (!form) return defaults;
          return {
            title: form.title || defaults.title,
            passing_score: form.passing_score ?? defaults.passing_score,
            time_limit: form.time_limit ?? defaults.time_limit,
            max_attempts: form.max_attempts ?? defaults.max_attempts,
            show_correct_answers: form.show_correct_answers ?? defaults.show_correct_answers,
            shuffle_questions: form.shuffle_questions ?? defaults.shuffle_questions,
            shuffle_options: form.shuffle_options ?? defaults.shuffle_options,
            questions: form.questions || [],
          };
        };
        
        // Normalize in_between module quizzes
        const normalizedModuleQuizzes = {};
        if (editingCourse.quizzes.in_between?.module_quizzes) {
          Object.entries(editingCourse.quizzes.in_between.module_quizzes).forEach(([key, quiz]) => {
            normalizedModuleQuizzes[key] = {
              ...normalizeQuizForm(quiz, {
                title: `Module ${parseInt(key) + 1} Quiz`,
                passing_score: 70,
                time_limit: 15,
                max_attempts: 3,
                show_correct_answers: true,
                shuffle_questions: false,
                shuffle_options: false,
                questions: [],
              }),
              after_module_index: quiz?.after_module_index ?? parseInt(key),
            };
          });
        }
        
        setQuizForms({
          pre_test: normalizeQuizForm(editingCourse.quizzes.pre_test, {
            title: 'Pre-Test',
            passing_score: 70,
            time_limit: 30,
            max_attempts: 3,
            show_correct_answers: true,
            shuffle_questions: false,
            shuffle_options: false,
            questions: [],
          }),
          in_between: {
            module_quizzes: normalizedModuleQuizzes,
          },
          post_test: normalizeQuizForm(editingCourse.quizzes.post_test, {
            title: 'Post-Test',
            passing_score: 70,
            time_limit: 30,
            max_attempts: 3,
            show_correct_answers: true,
            shuffle_questions: false,
            shuffle_options: false,
            questions: [],
          }),
        });
        
        // Set first selected type as active for editing
        if (typesToSelect.length > 0) {
          setActiveQuizType(typesToSelect[0]);
        }
      }
      // Legacy: Load old single quiz structure for backward compatibility
      else if (editingCourse.quiz && editingCourse.quiz.questions?.length > 0) {
        setQuizEnabled(true);
        setQuizForm({
          title: editingCourse.quiz.title || '',
          passing_score: editingCourse.quiz.passing_score || 70,
          time_limit: editingCourse.quiz.time_limit || 30,
          max_attempts: editingCourse.quiz.max_attempts || 3,
          show_correct_answers: editingCourse.quiz.show_correct_answers ?? true,
          shuffle_questions: editingCourse.quiz.shuffle_questions || false,
          shuffle_options: editingCourse.quiz.shuffle_options || false,
          questions: editingCourse.quiz.questions || [],
        });
        // For legacy quizzes, treat as post_test by default
        setSelectedQuizTypes(['post_test']);
        setActiveQuizType('post_test');
        setQuizForms(prev => ({
          ...prev,
          post_test: {
            title: editingCourse.quiz.title || 'Post-Test',
            passing_score: editingCourse.quiz.passing_score || 70,
            time_limit: editingCourse.quiz.time_limit || 30,
            max_attempts: editingCourse.quiz.max_attempts || 3,
            show_correct_answers: editingCourse.quiz.show_correct_answers ?? true,
            shuffle_questions: editingCourse.quiz.shuffle_questions || false,
            shuffle_options: editingCourse.quiz.shuffle_options || false,
            questions: editingCourse.quiz.questions || [],
          },
        }));
      }
    } else {
      // Reset all forms for new course
      setCourseId(null);
      setCourseForm({
        title: '',
        description: '',
        category: '',
        difficulty: 'Beginner',
        duration_minutes: 30,
        points_reward: 100,
        thumbnail_url: '',
        is_published: false,
      });
      setModules([]);
      setQuizEnabled(false);
      setSelectedQuizTypes([]);
      setActiveQuizType(null);
      setSelectedModuleForQuiz(null);
      setQuizForm({
        title: '',
        passing_score: 70,
        time_limit: 30,
        max_attempts: 3,
        show_correct_answers: true,
        shuffle_questions: false,
        shuffle_options: false,
        questions: [],
      });
      setQuizForms({
        pre_test: {
          title: 'Pre-Test',
          passing_score: 70,
          time_limit: 30,
          max_attempts: 3,
          show_correct_answers: true,
          shuffle_questions: false,
          shuffle_options: false,
          questions: [],
        },
        in_between: {
          module_quizzes: {},
        },
        post_test: {
          title: 'Post-Test',
          passing_score: 70,
          time_limit: 30,
          max_attempts: 3,
          show_correct_answers: true,
          shuffle_questions: false,
          shuffle_options: false,
          questions: [],
        },
      });
      setAssignmentForm({
        roles: [],
        due_date: null,
      });
    }
    setCurrentStep(1);
  }, [editingCourse, open]);

  const steps = [
    { number: 1, title: t('courses.wizard.courseInfo', 'Course Info'), icon: BookOpen },
    { number: 2, title: t('courses.wizard.modules', 'Modules'), icon: Video },
    { number: 3, title: t('courses.wizard.quiz', 'Quiz'), icon: Brain },
    { number: 4, title: t('courses.wizard.assignment', 'Assignment'), icon: Users, optional: true },
  ];

  // Video upload handler
  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast.error(t('courses.invalidVideoFile', 'Please upload a video file'));
      return;
    }

    if (file.size > 500 * 1024 * 1024) {
      toast.error(t('courses.videoTooLarge', 'Video file must be less than 500MB'));
      return;
    }

    setUploadingVideo(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/upload/video`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      setModuleForm(prev => ({
        ...prev,
        video_url: `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`,
        videoFile: true, // Flag to indicate uploaded video
      }));
      toast.success(t('courses.videoUploaded', 'Video uploaded successfully'));
      setModuleContentError(''); // Clear any error
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to upload video';
      toast.error(errorMessage);
      console.error('Video upload error:', errorMessage);
    } finally {
      setUploadingVideo(false);
      setUploadProgress(0);
    }
  };

  // Thumbnail upload handler
  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('courses.invalidImageFile', 'Please upload an image file'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('courses.imageTooLarge', 'Image file must be less than 5MB'));
      return;
    }

    setUploadingThumbnail(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/upload/image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setCourseForm(prev => ({
        ...prev,
        thumbnail_url: `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`,
      }));
      toast.success(t('courses.thumbnailUploaded', 'Thumbnail uploaded successfully'));
    } catch (error) {
      toast.error(t('courses.uploadFailed', 'Failed to upload thumbnail'));
    } finally {
      setUploadingThumbnail(false);
    }
  };

  // Document (PDF/PPT) upload handler
  const handleDocumentUpload = async (e, fileType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedExtensions = fileType === 'pdf' ? ['.pdf'] : ['.ppt', '.pptx'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedExtensions.includes(ext)) {
      toast.error(t('courses.invalidDocumentFile', `Please upload a ${fileType.toUpperCase()} file`));
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error(t('courses.documentTooLarge', 'Document file must be less than 50MB'));
      return;
    }

    setUploadingDocument(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/upload/document`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      // Handle PPT to PDF conversion
      // If it was a PPT file that got converted, update the module type to 'pdf' for proper viewing
      const wasConverted = response.data.converted && response.data.original_type === 'ppt';
      
      setModuleForm(prev => ({
        ...prev,
        content: `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`,
        original_filename: response.data.original_name,
        // If PPT was converted to PDF, change the type to 'pdf' for proper embedded viewing
        // But keep track of original type for display purposes
        ...(wasConverted && fileType === 'ppt' ? { 
          type: 'pdf',
          original_type: 'ppt',
          original_url: response.data.original_url ? `${process.env.REACT_APP_BACKEND_URL}${response.data.original_url}` : null
        } : {})
      }));
      
      if (wasConverted) {
        toast.success(t('courses.pptConvertedToPdf', 'PowerPoint converted to PDF for embedded viewing'));
      } else {
        toast.success(t('courses.documentUploaded', 'Document uploaded successfully'));
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || t('courses.uploadFailed', 'Failed to upload document');
      toast.error(errorMsg);
    } finally {
      setUploadingDocument(false);
    }
  };

  // Audio upload handler
  const handleAudioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedExtensions = ['.mp3', '.wav', '.ogg', '.aac', '.m4a'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedExtensions.includes(ext) && !file.type.startsWith('audio/')) {
      toast.error(t('courses.invalidAudioFile', 'Please upload an audio file (MP3, WAV, OGG, AAC, M4A)'));
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast.error(t('courses.audioTooLarge', 'Audio file must be less than 50MB'));
      return;
    }

    setUploadingAudio(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/upload/audio`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setModuleForm(prev => ({
        ...prev,
        content: `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`,
        original_filename: response.data.original_name,
      }));
      toast.success(t('courses.audioUploaded', 'Audio uploaded successfully'));
    } catch (error) {
      toast.error(t('courses.uploadFailed', 'Failed to upload audio'));
    } finally {
      setUploadingAudio(false);
    }
  };

  // Module type selection handler
  const handleModuleTypeSelect = (type) => {
    setModuleForm({
      title: '',
      type: type,
      content: '',
      video_url: '',
      videoFile: null,
    });
    setModuleTitleErrors([]);
    setModuleContentError('');
    setShowModuleTypeModal(false);
    setShowModuleFormModal(true);
  };

  // Open module type modal for new module
  const handleOpenAddModule = () => {
    setEditingModuleIndex(null);
    setShowModuleTypeModal(true);
  };

  // Close module form modal
  const handleCloseModuleForm = () => {
    setShowModuleFormModal(false);
    setEditingModuleIndex(null);
    setModuleForm({
      title: '',
      type: 'video',
      content: '',
      video_url: '',
      videoFile: null,
    });
    setModuleTitleErrors([]);
    setModuleContentError('');
  };

  // Module handlers
  const handleAddModule = () => {
    // Validate module title (min 10, max 30)
    const titleValidation = validateTitle(moduleForm.title, 10, 30);
    if (!titleValidation.isValid) {
      setModuleTitleErrors(titleValidation.errors);
      toast.error(titleValidation.errors[0]);
      return;
    }

    // Check for duplicate module title
    const duplicateExists = modules.some((m, idx) => 
      m.title.toLowerCase().trim() === moduleForm.title.toLowerCase().trim() && 
      idx !== editingModuleIndex
    );
    if (duplicateExists) {
      setModuleTitleErrors(['A module with this title already exists']);
      toast.error(t('courses.duplicateModuleTitle', 'A module with this title already exists'));
      return;
    }

    // Validate content based on type
    if (moduleForm.type === 'video') {
      // Check if both video upload and URL are blank
      if (!moduleForm.video_url && !moduleForm.videoFile) {
        setModuleContentError('Please upload a video file OR provide a video URL. At least one is required.');
        toast.error(t('courses.videoRequired', 'Please upload a video file OR provide a video URL'));
        return;
      }
      // If URL is provided, validate it
      if (moduleForm.video_url && !isValidVideoUrl(moduleForm.video_url)) {
        setModuleContentError('Invalid video URL. Please provide a valid YouTube, Vimeo, or direct video link.');
        toast.error(t('courses.invalidVideoUrl', 'Please provide a valid video URL'));
        return;
      }
    } else if (moduleForm.type === 'text') {
      if (!moduleForm.content || moduleForm.content.trim().length < 10) {
        setModuleContentError('Text content is required (minimum 10 characters)');
        toast.error(t('courses.textContentRequired', 'Text content is required (minimum 10 characters)'));
        return;
      }
    } else if (moduleForm.type === 'link') {
      if (!moduleForm.content || !/^https?:\/\/.+/i.test(moduleForm.content.trim())) {
        setModuleContentError('Please provide a valid URL starting with http:// or https://');
        toast.error(t('courses.validLinkRequired', 'Please provide a valid URL'));
        return;
      }
    } else if (moduleForm.type === 'pdf') {
      if (!moduleForm.content || !moduleForm.content.includes('/uploads/documents/')) {
        setModuleContentError('Please upload a PDF file');
        toast.error(t('courses.pdfRequired', 'Please upload a PDF file'));
        return;
      }
    } else if (moduleForm.type === 'ppt') {
      if (!moduleForm.content || !moduleForm.content.includes('/uploads/documents/')) {
        setModuleContentError('Please upload a PowerPoint file');
        toast.error(t('courses.pptRequired', 'Please upload a PowerPoint file'));
        return;
      }
    } else if (moduleForm.type === 'audio') {
      if (!moduleForm.content || !moduleForm.content.includes('/uploads/audio/')) {
        setModuleContentError('Please upload an audio file');
        toast.error(t('courses.audioRequired', 'Please upload an audio file'));
        return;
      }
    } else if (moduleForm.type === 'slide') {
      // Slide content is optional - user can save without designing slides
      // The content will be JSON string with slides data
    }

    // Clear errors
    setModuleTitleErrors([]);
    setModuleContentError('');

    const newModule = {
      id: `module_${Date.now()}`,
      ...moduleForm,
      order: modules.length + 1,
    };

    if (editingModuleIndex !== null) {
      const updatedModules = [...modules];
      updatedModules[editingModuleIndex] = { ...updatedModules[editingModuleIndex], ...moduleForm };
      setModules(updatedModules);
      setEditingModuleIndex(null);
    } else {
      setModules([...modules, newModule]);
    }

    setModuleForm({
      title: '',
      type: 'video',
      content: '',
      video_url: '',
      videoFile: null,
    });
    
    // Close modal after adding
    setShowModuleFormModal(false);
  };

  const handleEditModule = (index) => {
    const module = modules[index];
    setModuleForm({
      title: module.title,
      type: module.type,
      content: module.content || '',
      video_url: module.video_url || '',
      videoFile: null,
    });
    setEditingModuleIndex(index);
    setModuleTitleErrors([]);
    setModuleContentError('');
    setShowModuleFormModal(true);
  };

  const handleDeleteModule = (index) => {
    setModules(modules.filter((_, i) => i !== index));
    if (editingModuleIndex === index) {
      setEditingModuleIndex(null);
      setModuleForm({
        title: '',
        type: 'video',
        content: '',
        video_url: '',
        videoFile: null,
      });
    }
  };

  // AI Quiz generation
  const handleGenerateQuiz = async () => {
    if (modules.length === 0) {
      toast.error(t('courses.addModulesFirst', 'Please add modules first to generate quiz'));
      return;
    }

    // Collect text content from all modules
    const moduleContents = modules.map(m => {
      let content = `Module: ${m.title}\n`;
      if (m.type === 'text' && m.content) {
        content += `Content: ${m.content}\n`;
      } else if (m.type === 'link' && m.content) {
        content += `Resource Link: ${m.content}\n`;
      } else if (m.type === 'video') {
        content += `Video content (title context): ${m.title}\n`;
      }
      return content;
    }).join('\n---\n');

    setGeneratingQuiz(true);
    try {
      const token = localStorage.getItem('token');
      const context = `
Course Title: ${courseForm.title}
Course Description: ${courseForm.description || 'No description provided'}
Category: ${courseForm.category}
Difficulty: ${courseForm.difficulty}

Module Contents:
${moduleContents}
      `.trim();
      
      const response = await axios.post(`${API_URL}/ai/quiz/generate`, {
        context: context,
        num_questions: 5,
        question_types: ['multiple_choice', 'true_false'],
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setQuizForm(prev => ({
        ...prev,
        title: `${courseForm.title} Quiz`,
        questions: response.data.questions || [],
      }));
      toast.success(t('courses.quizGenerated', 'Quiz generated successfully!'));
    } catch (error) {
      toast.error(t('courses.quizGenerationFailed', 'Failed to generate quiz. Please try again or add questions manually.'));
    } finally {
      setGeneratingQuiz(false);
    }
  };

  // Add question manually
  const handleAddQuestion = () => {
    // Validate the last question before adding a new one
    if (!validateCurrentQuestion()) {
      return;
    }
    
    const newQuestion = {
      id: `q_${Date.now()}`,
      type: 'single_choice',
      question: '',
      options: ['', ''], // Start with 2 empty options
      correct_answer: 0,
      correct_answers: [], // For multiple choice
      explanation: '',
      points: 10,
    };
    setQuizForm(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion],
    }));
  };

  const handleUpdateQuestion = (index, updates) => {
    const updatedQuestions = [...quizForm.questions];
    updatedQuestions[index] = { ...updatedQuestions[index], ...updates };
    setQuizForm(prev => ({ ...prev, questions: updatedQuestions }));
  };

  const handleDeleteQuestion = (index) => {
    setQuizForm(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index),
    }));
  };

  // Quiz type-specific handlers
  const handleAddQuestionForType = (quizType, questionType = 'single_choice') => {
    const newQuestion = {
      id: `q_${Date.now()}`,
      type: questionType,
      question: '',
      options: questionType === 'true_false' ? ['True', 'False'] : ['', '', '', ''],
      correct_answer: 0,
      correct_answers: [],
      explanation: '',
      points: 10,
      pairs: questionType === 'match_following' ? [{ left: '', right: '' }, { left: '', right: '' }] : undefined,
    };
    
    // Handle in_between module-specific quizzes
    if (quizType === 'in_between' && selectedModuleForQuiz !== null) {
      setQuizForms(prev => ({
        ...prev,
        in_between: {
          ...prev.in_between,
          module_quizzes: {
            ...prev.in_between.module_quizzes,
            [selectedModuleForQuiz]: {
              ...(prev.in_between.module_quizzes[selectedModuleForQuiz] || getDefaultModuleQuiz(selectedModuleForQuiz)),
              questions: [
                ...((prev.in_between.module_quizzes[selectedModuleForQuiz]?.questions) || []),
                newQuestion
              ],
            }
          }
        },
      }));
    } else {
      setQuizForms(prev => ({
        ...prev,
        [quizType]: {
          ...prev[quizType],
          questions: [...(prev[quizType]?.questions || []), newQuestion],
        },
      }));
    }
  };

  const handleUpdateQuestionForType = (quizType, index, updates) => {
    // Handle in_between module-specific quizzes
    if (quizType === 'in_between' && selectedModuleForQuiz !== null) {
      setQuizForms(prev => {
        const currentQuestions = [...(prev.in_between.module_quizzes[selectedModuleForQuiz]?.questions || [])];
        currentQuestions[index] = { ...currentQuestions[index], ...updates };
        return {
          ...prev,
          in_between: {
            ...prev.in_between,
            module_quizzes: {
              ...prev.in_between.module_quizzes,
              [selectedModuleForQuiz]: {
                ...prev.in_between.module_quizzes[selectedModuleForQuiz],
                questions: currentQuestions,
              }
            }
          },
        };
      });
    } else {
      setQuizForms(prev => {
        const currentQuestions = [...(prev[quizType]?.questions || [])];
        currentQuestions[index] = { ...currentQuestions[index], ...updates };
        return {
          ...prev,
          [quizType]: {
            ...prev[quizType],
            questions: currentQuestions,
          },
        };
      });
    }
  };

  const handleRemoveQuestionForType = (quizType, index) => {
    // Handle in_between module-specific quizzes
    if (quizType === 'in_between' && selectedModuleForQuiz !== null) {
      setQuizForms(prev => ({
        ...prev,
        in_between: {
          ...prev.in_between,
          module_quizzes: {
            ...prev.in_between.module_quizzes,
            [selectedModuleForQuiz]: {
              ...prev.in_between.module_quizzes[selectedModuleForQuiz],
              questions: (prev.in_between.module_quizzes[selectedModuleForQuiz]?.questions || []).filter((_, i) => i !== index),
            }
          }
        },
      }));
    } else {
      setQuizForms(prev => ({
        ...prev,
        [quizType]: {
          ...prev[quizType],
          questions: (prev[quizType]?.questions || []).filter((_, i) => i !== index),
        },
      }));
    }
  };

  const handleGenerateQuizForType = async (quizType) => {
    if (modules.length === 0) {
      toast.error(t('courses.addModulesFirst', 'Please add some modules first before generating quiz questions'));
      return;
    }
    setGeneratingQuiz(true);
    try {
      const token = localStorage.getItem('token');
      
      // For in_between, use only the selected module's content
      let moduleContents;
      if (quizType === 'in_between' && selectedModuleForQuiz !== null && modules[selectedModuleForQuiz]) {
        const selectedModule = modules[selectedModuleForQuiz];
        moduleContents = `${selectedModule.title}: ${selectedModule.description || ''}`;
      } else {
        moduleContents = modules.map(m => `${m.title}: ${m.description || ''}`).join('\n');
      }
      
      const response = await axios.post(
        `${API_URL}/ai/generate-quiz`,
        {
          course_title: courseForm.title,
          course_description: courseForm.description,
          module_contents: moduleContents,
          num_questions: 5,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data && response.data.questions) {
        const generatedQuestions = response.data.questions.map((q, idx) => ({
          id: `q_${Date.now()}_${idx}`,
          type: q.type || 'single_choice',
          question: q.question,
          options: q.options || ['', '', '', ''],
          correct_answer: q.correct_answer || 0,
          explanation: q.explanation || '',
          points: 10,
        }));

        // Handle in_between module-specific quizzes
        if (quizType === 'in_between' && selectedModuleForQuiz !== null) {
          setQuizForms(prev => ({
            ...prev,
            in_between: {
              ...prev.in_between,
              module_quizzes: {
                ...prev.in_between.module_quizzes,
                [selectedModuleForQuiz]: {
                  ...(prev.in_between.module_quizzes[selectedModuleForQuiz] || getDefaultModuleQuiz(selectedModuleForQuiz)),
                  questions: generatedQuestions,
                }
              }
            },
          }));
          toast.success(`Generated ${response.data.questions.length} questions for Module ${selectedModuleForQuiz + 1} Quiz`);
        } else {
          setQuizForms(prev => ({
            ...prev,
            [quizType]: {
              ...prev[quizType],
              questions: generatedQuestions,
            },
          }));
          toast.success(`Generated ${response.data.questions.length} questions for ${QUIZ_TYPE_OPTIONS.find(o => o.value === quizType)?.label}`);
        }
      }
    } catch (error) {
      toast.error('Failed to generate quiz questions');
    } finally {
      setGeneratingQuiz(false);
    }
  };

  // Save course (Step 1)
  const handleSaveCourse = async () => {
    // Validate course title
    const titleValidation = validateTitle(courseForm.title, 10, 100);
    if (!titleValidation.isValid) {
      setCourseTitleErrors(titleValidation.errors);
      toast.error(titleValidation.errors[0]);
      return false;
    }

    if (!courseForm.category) {
      toast.error(t('courses.categoryRequired', 'Please select a category'));
      return false;
    }

    // Validate duration (must be > 0)
    if (!courseForm.duration_minutes || courseForm.duration_minutes <= 0) {
      toast.error(t('courses.durationRequired', 'Duration must be greater than 0'));
      return false;
    }

    setCourseTitleErrors([]);
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      let response;
      
      if (courseId) {
        response = await axios.put(
          `${API_URL}/admin/courses/${courseId}`,
          courseForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(t('courses.updated', 'Course updated successfully'));
      } else {
        response = await axios.post(`${API_URL}/admin/courses`, courseForm, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCourseId(response.data.id);
        toast.success(t('courses.created', 'Course created successfully'));
      }
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || t('courses.saveFailed', 'Failed to save course'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Save modules (Step 2)
  const handleSaveModules = async () => {
    if (!courseId) {
      toast.error(t('courses.saveCourseFirst', 'Please save the course first'));
      return false;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Update course with modules
      await axios.put(
        `${API_URL}/admin/courses/${courseId}`,
        { modules: modules.map((m, i) => ({ ...m, order: i + 1 })) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(t('courses.modulesSaved', 'Modules saved successfully'));
      return true;
    } catch (error) {
      toast.error(t('courses.modulesSaveFailed', 'Failed to save modules'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Helper function to validate a single quiz's questions
  const validateQuizQuestions = (quizData, quizLabel) => {
    if (!quizData?.questions?.length) return true;
    
    for (let i = 0; i < quizData.questions.length; i++) {
      const q = quizData.questions[i];
      
      // Question text required
      if (!q.question || q.question.trim().length < 5) {
        toast.error(`${quizLabel}: Question ${i + 1} is incomplete. Please enter question text (min 5 chars).`);
        return false;
      }
      
      // Type-specific validation
      if (q.type === 'single_choice' || q.type === 'multiple_choice' || 
          q.type === 'image_based' || q.type === 'scenario') {
        const options = q.options || [];
        const filledOptions = options.filter(opt => opt && opt.trim().length > 0);
        if (filledOptions.length < 2) {
          toast.error(`${quizLabel}: Question ${i + 1}: Please fill at least 2 options.`);
          return false;
        }
      }
      
      if (q.type === 'match_following') {
        const pairs = q.match_pairs || [];
        const filledPairs = pairs.filter(p => p.left && p.left.trim() && p.right && p.right.trim());
        if (filledPairs.length < 2) {
          toast.error(`${quizLabel}: Question ${i + 1}: Please fill at least 2 match pairs.`);
          return false;
        }
      }
      
      if (q.type === 'image_based' && (!q.image_url || q.image_url.trim().length === 0)) {
        toast.error(`${quizLabel}: Question ${i + 1}: Please provide an image URL.`);
        return false;
      }
      
      if (q.type === 'scenario' && (!q.scenario || q.scenario.trim().length < 10)) {
        toast.error(`${quizLabel}: Question ${i + 1}: Please provide a scenario description.`);
        return false;
      }
    }
    return true;
  };

  // Save quiz (Step 3) - Now supports multi-quiz structure
  const handleSaveQuiz = async () => {
    if (!courseId) {
      toast.error(t('courses.saveCourseFirst', 'Please save the course first'));
      return false;
    }

    if (!quizEnabled || selectedQuizTypes.length === 0) {
      // Clear quizzes if disabled
      setSaving(true);
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          `${API_URL}/admin/courses/${courseId}`,
          { quizzes: null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        return true;
      } catch (error) {
        toast.error(t('courses.quizSaveFailed', 'Failed to save quiz'));
        return false;
      } finally {
        setSaving(false);
      }
    }

    // Build the quizzes structure based on selected types
    const quizzesPayload = {};

    // Validate and build Pre-test
    if (selectedQuizTypes.includes('pre_test')) {
      const preTestData = quizForms.pre_test;
      if (!validateQuizQuestions(preTestData, 'Pre-Test')) return false;
      quizzesPayload.pre_test = {
        title: preTestData.title || 'Pre-Test',
        passing_score: preTestData.passing_score ?? 70,
        time_limit: preTestData.time_limit ?? 30,
        max_attempts: preTestData.max_attempts ?? 3,
        show_correct_answers: preTestData.show_correct_answers ?? true,
        shuffle_questions: preTestData.shuffle_questions ?? false,
        shuffle_options: preTestData.shuffle_options ?? false,
        questions: preTestData.questions || [],
      };
    }

    // Validate and build Post-test
    if (selectedQuizTypes.includes('post_test')) {
      const postTestData = quizForms.post_test;
      if (!validateQuizQuestions(postTestData, 'Post-Test')) return false;
      quizzesPayload.post_test = {
        title: postTestData.title || 'Post-Test',
        passing_score: postTestData.passing_score ?? 70,
        time_limit: postTestData.time_limit ?? 30,
        max_attempts: postTestData.max_attempts ?? 3,
        show_correct_answers: postTestData.show_correct_answers ?? true,
        shuffle_questions: postTestData.shuffle_questions ?? false,
        shuffle_options: postTestData.shuffle_options ?? false,
        questions: postTestData.questions || [],
      };
    }

    // Validate and build In-between module quizzes
    if (selectedQuizTypes.includes('in_between')) {
      const moduleQuizzes = quizForms.in_between?.module_quizzes || {};
      const processedModuleQuizzes = {};
      
      for (const [moduleIndex, quizData] of Object.entries(moduleQuizzes)) {
        if (quizData?.questions?.length > 0) {
          const moduleName = modules[moduleIndex]?.title || `Module ${parseInt(moduleIndex) + 1}`;
          if (!validateQuizQuestions(quizData, `In-Between Quiz (after ${moduleName})`)) return false;
          
          processedModuleQuizzes[moduleIndex] = {
            title: quizData.title || `Quiz after ${moduleName}`,
            passing_score: quizData.passing_score ?? 70,
            time_limit: quizData.time_limit ?? 15,
            max_attempts: quizData.max_attempts ?? 3,
            show_correct_answers: quizData.show_correct_answers ?? true,
            shuffle_questions: quizData.shuffle_questions ?? false,
            shuffle_options: quizData.shuffle_options ?? false,
            questions: quizData.questions || [],
            after_module_index: parseInt(moduleIndex),
          };
        }
      }
      
      if (Object.keys(processedModuleQuizzes).length > 0) {
        quizzesPayload.in_between = {
          module_quizzes: processedModuleQuizzes,
        };
      }
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      await axios.put(
        `${API_URL}/admin/courses/${courseId}`,
        { quizzes: quizzesPayload },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(t('courses.quizSaved', 'Quiz configuration saved successfully'));
      return true;
    } catch (error) {
      console.error('Failed to save quiz:', error);
      toast.error(t('courses.quizSaveFailed', 'Failed to save quiz'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Assign course (Step 4)
  const handleAssignCourse = async () => {
    if (!courseId) return true;
    
    // Assignment is optional
    if (assignmentForm.roles.length === 0) {
      return true;
    }

    if (!assignmentForm.due_date) {
      toast.error(t('courses.dueDateRequired', 'Please select a due date'));
      return false;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      await axios.post(`${API_URL}/admin/courses/${courseId}/assign`, {
        assign_to: 'role',
        roles: assignmentForm.roles,
        due_date: assignmentForm.due_date.toISOString(),
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      toast.success(t('courses.assigned', 'Course assigned successfully'));
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || t('courses.assignFailed', 'Failed to assign course'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Navigation handlers
  const handleNext = async () => {
    let success = true;
    
    if (currentStep === 1) {
      success = await handleSaveCourse();
    } else if (currentStep === 2) {
      success = await handleSaveModules();
    } else if (currentStep === 3) {
      success = await handleSaveQuiz();
    }
    
    if (success && currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = async () => {
    let success = true;
    
    // First, save the course with is_published status
    if (courseId) {
      setSaving(true);
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          `${API_URL}/admin/courses/${courseId}`,
          { is_published: courseForm.is_published },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        toast.error(error.response?.data?.detail || t('courses.saveFailed', 'Failed to save course'));
        setSaving(false);
        return;
      }
      setSaving(false);
    }
    
    // Then handle assignment if on step 4
    if (currentStep === 4) {
      success = await handleAssignCourse();
    }
    
    if (success) {
      toast.success(editingCourse 
        ? t('courses.updateComplete', 'Course updated successfully!') 
        : t('courses.createComplete', 'Course created successfully!')
      );
      onSaveComplete?.();
      onOpenChange(false);
    }
  };

  const handleSkipAssignment = async () => {
    // Save the course with is_published status before skipping
    if (courseId) {
      try {
        const token = localStorage.getItem('token');
        await axios.put(
          `${API_URL}/admin/courses/${courseId}`,
          { is_published: courseForm.is_published },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Failed to save publish status:', error);
      }
    }
    
    toast.success(editingCourse 
      ? t('courses.updateComplete', 'Course updated successfully!') 
      : t('courses.createComplete', 'Course created successfully!')
    );
    onSaveComplete?.();
    onOpenChange(false);
  };

  // Step 1: Course Info validation
  const isStep1Valid = () => {
    const titleValidation = validateTitle(courseForm.title, 10, 100);
    if (!titleValidation.isValid) return false;
    if (!courseForm.category) return false;
    if (!courseForm.duration_minutes || courseForm.duration_minutes <= 0) return false;
    return true;
  };

  // Step 2: Modules validation
  const isStep2Valid = () => {
    // User must add at least one module
    return modules.length > 0;
  };

  // Step 3: Quiz validation
  const isStep3Valid = () => {
    if (!quizEnabled) return true; // If quiz disabled, step is valid
    
    // If quiz is enabled but no types selected, not valid
    if (selectedQuizTypes.length === 0) return false;
    
    // Check each selected quiz type has at least one question
    for (const type of selectedQuizTypes) {
      if (type === 'in_between') {
        // For in_between, check if at least one module has questions
        const moduleQuizzes = quizForms.in_between?.module_quizzes || {};
        const hasAnyModuleQuiz = Object.values(moduleQuizzes).some(quiz => quiz?.questions?.length > 0);
        if (!hasAnyModuleQuiz) return false;
      } else {
        // For pre_test and post_test, check if questions exist
        const form = quizForms[type];
        if (!form || !form.questions || form.questions.length === 0) return false;
        
        // Validate quiz settings (only check if explicitly set to invalid values, not if undefined)
        if (form.time_limit !== undefined && form.time_limit !== null && form.time_limit <= 0) return false;
        if (form.max_attempts !== undefined && form.max_attempts !== null && form.max_attempts <= 0) return false;
        
        // Validate each question
        for (const q of form.questions) {
          if (!q.question || q.question.trim().length < 5) return false;
          
          if (q.type === 'single_choice' || q.type === 'multiple_choice') {
            const options = q.options || [];
            const filledOptions = options.filter(opt => opt && opt.trim().length > 0);
            if (filledOptions.length < 2) return false;
          }
          
          if (q.type === 'match_following') {
            const pairs = q.match_pairs || []; // Fixed: was q.pairs, should be q.match_pairs
            const filledPairs = pairs.filter(p => p.left && p.left.trim() && p.right && p.right.trim());
            if (filledPairs.length < 2) return false;
          }
        }
      }
    }
    
    return true;
  };

  // Check if current step is valid
  const isCurrentStepValid = () => {
    switch (currentStep) {
      case 1: return isStep1Valid();
      case 2: return isStep2Valid();
      case 3: return isStep3Valid();
      case 4: return true; // Assignment is optional
      default: return true;
    }
  };

  // Validate question before adding another
  const validateCurrentQuestion = () => {
    if (quizForm.questions.length === 0) return true;
    
    const lastQuestion = quizForm.questions[quizForm.questions.length - 1];
    
    // Question text required
    if (!lastQuestion.question || lastQuestion.question.trim().length < 5) {
      toast.error(t('courses.questionRequired', 'Please enter a question (minimum 5 characters)'));
      return false;
    }
    
    // Type-specific validation
    if (lastQuestion.type === 'single_choice' || lastQuestion.type === 'multiple_choice' || 
        lastQuestion.type === 'image_based' || lastQuestion.type === 'scenario') {
      const options = lastQuestion.options || [];
      const filledOptions = options.filter(opt => opt && opt.trim().length > 0);
      
      // Check for minimum options
      if (filledOptions.length < 2) {
        toast.error(t('courses.optionsRequired', 'Please fill at least 2 options'));
        return false;
      }
      
      // Check for duplicate options
      const uniqueOptions = new Set(filledOptions.map(o => o.trim().toLowerCase()));
      if (uniqueOptions.size !== filledOptions.length) {
        toast.error(t('courses.duplicateOptions', 'Options cannot be duplicates. Each option must be unique.'));
        return false;
      }
      
      // Check for multiple choice - at least one correct answer
      if (lastQuestion.type === 'multiple_choice') {
        const correctAnswers = lastQuestion.correct_answers || [];
        if (correctAnswers.length === 0) {
          toast.error(t('courses.selectCorrectAnswer', 'Please select at least one correct answer'));
          return false;
        }
      }
    }
    
    if (lastQuestion.type === 'match_following') {
      const pairs = lastQuestion.match_pairs || [];
      const filledPairs = pairs.filter(p => p.left && p.left.trim() && p.right && p.right.trim());
      if (filledPairs.length < 2) {
        toast.error(t('courses.matchPairsRequired', 'Please fill at least 2 match pairs'));
        return false;
      }
    }
    
    if (lastQuestion.type === 'image_based' && (!lastQuestion.image_url || lastQuestion.image_url.trim().length === 0)) {
      toast.error(t('courses.imageUrlRequired', 'Please provide an image URL'));
      return false;
    }
    
    if (lastQuestion.type === 'scenario' && (!lastQuestion.scenario || lastQuestion.scenario.trim().length < 10)) {
      toast.error(t('courses.scenarioRequired', 'Please provide a scenario description (minimum 10 characters)'));
      return false;
    }
    
    return true;
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderCourseInfoStep();
      case 2:
        return renderModulesStep();
      case 3:
        return renderQuizStep();
      case 4:
        return renderAssignmentStep();
      default:
        return null;
    }
  };

  // Step 1: Course Info
  const renderCourseInfoStep = () => (
    <div className="space-y-4 py-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2 space-y-2">
          <Label htmlFor="title">{t('courses.title', 'Course Title')} *</Label>
          <Input
            id="title"
            value={courseForm.title}
            onChange={(e) => {
              setCourseForm(prev => ({ ...prev, title: e.target.value }));
              // Real-time validation
              const validation = validateTitle(e.target.value, 10, 100);
              setCourseTitleErrors(validation.isValid ? [] : validation.errors);
            }}
            placeholder={t('courses.titlePlaceholder', 'e.g., Customer Service Excellence')}
            className={courseTitleErrors.length > 0 ? 'border-destructive' : ''}
            data-testid="wizard-course-title"
          />
          {courseTitleErrors.length > 0 && (
            <div className="flex items-start gap-1.5 text-destructive text-xs">
              <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>{courseTitleErrors[0]}</span>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            {t('courses.titleHint', '10-100 characters. Must contain letters, no repeated characters.')}
          </p>
        </div>
        <div className="col-span-2 space-y-2">
          <Label htmlFor="description">{t('courses.description', 'Description')}</Label>
          <Textarea
            id="description"
            value={courseForm.description}
            onChange={(e) => setCourseForm(prev => ({ ...prev, description: e.target.value }))}
            placeholder={t('courses.descriptionPlaceholder', 'What will learners gain from this course?')}
            rows={3}
            data-testid="wizard-course-description"
          />
        </div>
        <div className="space-y-2">
          <Label>{t('courses.category', 'Category')} *</Label>
          <Select
            value={courseForm.category}
            onValueChange={(value) => setCourseForm(prev => ({ ...prev, category: value }))}
          >
            <SelectTrigger data-testid="wizard-course-category">
              <SelectValue placeholder={t('courses.selectCategory', 'Select category')} />
            </SelectTrigger>
            <SelectContent>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{t('courses.difficulty', 'Difficulty')}</Label>
          <Select
            value={courseForm.difficulty}
            onValueChange={(value) => setCourseForm(prev => ({ ...prev, difficulty: value }))}
          >
            <SelectTrigger data-testid="wizard-course-difficulty">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DIFFICULTIES.map(diff => (
                <SelectItem key={diff} value={diff}>{diff}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="duration">{t('courses.durationMinutes', 'Duration (Minutes)')} *</Label>
          <Input
            id="duration"
            type="number"
            value={courseForm.duration_minutes}
            onChange={(e) => setCourseForm(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 0 }))}
            min={1}
            data-testid="wizard-course-duration"
          />
          {courseForm.duration_minutes <= 0 && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {t('courses.durationError', 'Duration must be greater than 0')}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="points">{t('courses.pointsReward', 'Points Reward')}</Label>
          <Input
            id="points"
            type="number"
            value={courseForm.points_reward}
            onChange={(e) => setCourseForm(prev => ({ ...prev, points_reward: parseInt(e.target.value) || 100 }))}
            min={0}
            data-testid="wizard-course-points"
          />
        </div>
        <div className="col-span-2 space-y-2">
          <Label htmlFor="thumbnail">{t('courses.thumbnailUrl', 'Thumbnail')}</Label>
          <div className="flex gap-2">
            <div className="flex-1 space-y-2">
              <Input
                id="thumbnail"
                value={courseForm.thumbnail_url}
                onChange={(e) => setCourseForm(prev => ({ ...prev, thumbnail_url: e.target.value }))}
                placeholder="https://example.com/image.jpg"
                data-testid="wizard-course-thumbnail"
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => thumbnailInputRef.current?.click()}
                disabled={uploadingThumbnail}
                className="flex-shrink-0"
                data-testid="wizard-upload-thumbnail-btn"
              >
                {uploadingThumbnail ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
              </Button>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                ref={thumbnailInputRef}
                onChange={handleThumbnailUpload}
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('courses.thumbnailHint', 'Enter a URL or upload an image (Max 5MB)')}
          </p>
          {courseForm.thumbnail_url && (
            <div className="mt-2 p-2 border rounded-lg bg-muted/30">
              <img 
                src={courseForm.thumbnail_url} 
                alt="Thumbnail preview" 
                className="max-h-24 object-contain mx-auto rounded"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling?.classList.remove('hidden');
                }}
              />
              <p className="text-xs text-center text-muted-foreground mt-1 hidden">
                Unable to load preview
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Module types configuration for selection
  const MODULE_TYPES = [
    { type: 'video', icon: Video, label: t('courses.moduleTypes.video', 'Video'), description: t('courses.moduleTypes.videoDesc', 'Upload or link a video') },
    { type: 'pdf', icon: FileType, label: t('courses.moduleTypes.pdf', 'PDF Document'), description: t('courses.moduleTypes.pdfDesc', 'Upload a PDF file (embedded viewer)') },
    { type: 'ppt', icon: FileSpreadsheet, label: t('courses.moduleTypes.ppt', 'PowerPoint'), description: t('courses.moduleTypes.pptDesc', 'Upload a PPT/PPTX presentation') },
    { type: 'slide', icon: Presentation, label: t('courses.moduleTypes.slide', 'Slide Designer'), description: t('courses.moduleTypes.slideDesc', 'Create interactive slides with fabric.js') },
    { type: 'audio', icon: Headphones, label: t('courses.moduleTypes.audio', 'Audio'), description: t('courses.moduleTypes.audioDesc', 'Upload an audio file (MP3, WAV)') },
    { type: 'text', icon: FileText, label: t('courses.moduleTypes.text', 'Text/Notes'), description: t('courses.moduleTypes.textDesc', 'Add text content or notes') },
    { type: 'link', icon: LinkIcon, label: t('courses.moduleTypes.link', 'External Link'), description: t('courses.moduleTypes.linkDesc', 'Link to external resources') },
  ];

  // Step 2: Modules (with modal-based flow)
  const renderModulesStep = () => (
    <div className="space-y-4 py-4">
      {/* Module List */}
      {modules.length > 0 ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-base">{t('courses.modules', 'Modules')} ({modules.length})</Label>
          </div>
          <div className="space-y-2">
            {modules.map((module, index) => (
              <div
                key={module.id || index}
                className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent/5 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    {module.type === 'video' && <Video className="w-4 h-4 text-primary" />}
                    {module.type === 'text' && <FileText className="w-4 h-4 text-primary" />}
                    {module.type === 'link' && <LinkIcon className="w-4 h-4 text-primary" />}
                    {module.type === 'pdf' && <FileType className="w-4 h-4 text-primary" />}
                    {module.type === 'ppt' && <FileSpreadsheet className="w-4 h-4 text-primary" />}
                    {module.type === 'audio' && <Headphones className="w-4 h-4 text-primary" />}
                    {module.type === 'slide' && <Presentation className="w-4 h-4 text-primary" />}
                    {module.type === 'quiz' && <FileQuestion className="w-4 h-4 text-primary" />}
                  </div>
                  <div>
                    <p className="font-medium text-sm">{module.title}</p>
                    <p className="text-xs text-muted-foreground capitalize">{module.type === 'ppt' ? 'PowerPoint' : module.type === 'slide' ? 'Slide Designer' : module.type}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handleEditModule(index)} data-testid={`edit-module-${index}`}>
                    {t('common.edit', 'Edit')}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteModule(index)} data-testid={`delete-module-${index}`}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-8 border-2 border-dashed rounded-lg">
          <Video className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground mb-2">{t('courses.noModulesYet', 'No modules added yet')}</p>
          <p className="text-xs text-muted-foreground mb-4">{t('courses.addModulesHint', 'Click the button below to add your first module')}</p>
        </div>
      )}

      {/* Add Module Button */}
      <Button 
        onClick={handleOpenAddModule} 
        className="w-full" 
        size="lg"
        data-testid="add-module-btn"
      >
        <Plus className="w-4 h-4 mr-2" />
        {t('courses.addModule', 'Add Module')}
      </Button>

      {/* Module Type Selection Modal */}
      <Dialog open={showModuleTypeModal} onOpenChange={setShowModuleTypeModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              {t('courses.selectModuleType', 'Select Module Type')}
            </DialogTitle>
            <DialogDescription>
              {t('courses.selectModuleTypeDesc', 'Choose the type of content you want to add')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-2 py-4 max-h-[60vh] overflow-y-auto">
            {MODULE_TYPES.map((moduleType) => (
              <button
                key={moduleType.type}
                onClick={() => handleModuleTypeSelect(moduleType.type)}
                className="flex items-center gap-4 p-4 rounded-lg border hover:border-primary hover:bg-primary/5 transition-all text-left group"
                data-testid={`select-module-type-${moduleType.type}`}
              >
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <moduleType.icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium">{moduleType.label}</p>
                  <p className="text-xs text-muted-foreground">{moduleType.description}</p>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Module Form Modal */}
      <Dialog open={showModuleFormModal} onOpenChange={(open) => {
        if (!open) handleCloseModuleForm();
      }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {moduleForm.type === 'video' && <Video className="w-5 h-5" />}
              {moduleForm.type === 'text' && <FileText className="w-5 h-5" />}
              {moduleForm.type === 'link' && <LinkIcon className="w-5 h-5" />}
              {moduleForm.type === 'pdf' && <FileType className="w-5 h-5" />}
              {moduleForm.type === 'ppt' && <FileSpreadsheet className="w-5 h-5" />}
              {moduleForm.type === 'audio' && <Headphones className="w-5 h-5" />}
              {moduleForm.type === 'slide' && <Presentation className="w-5 h-5" />}
              {editingModuleIndex !== null 
                ? t('courses.editModule', 'Edit Module') 
                : t('courses.addNewModule', 'Add New Module')}
            </DialogTitle>
            <DialogDescription>
              {moduleForm.type === 'video' && t('courses.addVideoDesc', 'Upload a video or provide a video URL')}
              {moduleForm.type === 'text' && t('courses.addTextDesc', 'Add text content for this module')}
              {moduleForm.type === 'link' && t('courses.addLinkDesc', 'Add an external link to resources')}
              {moduleForm.type === 'pdf' && t('courses.addPdfDesc', 'Upload a PDF document for embedded viewing')}
              {moduleForm.type === 'ppt' && t('courses.addPptDesc', 'Upload a PowerPoint presentation')}
              {moduleForm.type === 'audio' && t('courses.addAudioDesc', 'Upload an audio file for playback')}
              {moduleForm.type === 'slide' && t('courses.addSlideDesc', 'Design interactive slides with fabric.js canvas')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Module Title */}
            <div className="space-y-2">
              <Label>{t('courses.moduleTitle', 'Module Title')} *</Label>
              <Input
                value={moduleForm.title}
                onChange={(e) => {
                  setModuleForm(prev => ({ ...prev, title: e.target.value }));
                  const validation = validateTitle(e.target.value, 10, 30);
                  setModuleTitleErrors(validation.isValid ? [] : validation.errors);
                }}
                placeholder={t('courses.moduleTitlePlaceholder', 'e.g., Introduction Video')}
                className={moduleTitleErrors.length > 0 ? 'border-destructive' : ''}
                data-testid="wizard-module-title"
              />
              {moduleTitleErrors.length > 0 && (
                <div className="flex items-start gap-1.5 text-destructive text-xs">
                  <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span>{moduleTitleErrors[0]}</span>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {t('courses.moduleTitleHint', '10-30 characters required')}
              </p>
            </div>

            {/* Video Content Form */}
            {moduleForm.type === 'video' && (
              <div className="space-y-3">
                <Label>{t('courses.videoContent', 'Video Content')} *</Label>
                <div className="border-2 border-dashed rounded-lg p-4 text-center">
                  {uploadingVideo ? (
                    <div className="space-y-2">
                      <Loader2 className="w-6 h-6 mx-auto animate-spin text-primary" />
                      <Progress value={uploadProgress} className="w-full" />
                      <p className="text-sm text-muted-foreground">{t('courses.uploading', 'Uploading...')} {uploadProgress}%</p>
                    </div>
                  ) : moduleForm.videoFile || (moduleForm.video_url && moduleForm.video_url.includes('/uploads/')) ? (
                    <div className="space-y-2">
                      <CheckCircle2 className="w-6 h-6 mx-auto text-green-500" />
                      <p className="text-sm text-green-600">{t('courses.videoUploaded', 'Video uploaded successfully')}</p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setModuleForm(prev => ({ ...prev, video_url: '', videoFile: null }))}
                      >
                        {t('courses.removeVideo', 'Remove & Upload New')}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Upload className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => videoInputRef.current?.click()}
                      >
                        {t('courses.uploadVideo', 'Upload Video')}
                      </Button>
                      <p className="text-xs text-muted-foreground mt-2">
                        {t('courses.supportedFormats', 'Supported: MP4, WebM, MOV, AVI (Max 500MB)')}
                      </p>
                      <input
                        type="file"
                        accept=".mp4,.webm,.mov,.avi,.mkv,.m4v,.wmv,video/*"
                        className="hidden"
                        ref={videoInputRef}
                        onChange={handleVideoUpload}
                      />
                    </>
                  )}
                </div>
                
                {!moduleForm.videoFile && !(moduleForm.video_url && moduleForm.video_url.includes('/uploads/')) && (
                  <>
                    <div className="text-center text-xs text-muted-foreground">{t('common.or', 'OR')}</div>
                    <div className="space-y-1">
                      <Input
                        value={moduleForm.video_url}
                        onChange={async (e) => {
                          const url = e.target.value;
                          setModuleForm(prev => ({ ...prev, video_url: url }));
                          setContentWarning(null);
                          
                          if (url && isSuspiciousUrl(url)) {
                            setContentWarning({
                              type: 'warning',
                              message: 'This URL contains suspicious patterns. Please verify the content is appropriate.'
                            });
                          }
                          
                          if (url && url.length > 10 && !url.includes('youtube') && !url.includes('vimeo')) {
                            setCheckingContent(true);
                            try {
                              const result = await moderateUrl(url, 'video_url');
                              if (!result.is_safe) {
                                setContentWarning({
                                  type: 'error',
                                  message: result.message || 'This content has been flagged as inappropriate',
                                  categories: result.categories_flagged
                                });
                                toast.error('Content Warning: ' + result.message);
                              }
                            } catch (err) {
                              // Silently fail moderation
                            } finally {
                              setCheckingContent(false);
                            }
                          }
                        }}
                        placeholder={t('courses.videoUrlPlaceholder', 'YouTube/Vimeo URL or direct video link')}
                        data-testid="wizard-module-video-url"
                        className={contentWarning?.type === 'error' ? 'border-destructive' : ''}
                      />
                      <p className="text-xs text-muted-foreground">
                        {t('courses.videoUrlHint', 'Supported: YouTube, Vimeo, or direct video file URLs')}
                      </p>
                      {checkingContent && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          Checking content safety...
                        </div>
                      )}
                    </div>
                  </>
                )}
                
                {contentWarning && (
                  <div className={`flex items-start gap-2 p-3 rounded-lg ${
                    contentWarning.type === 'error' 
                      ? 'bg-destructive/10 text-destructive border border-destructive/20' 
                      : 'bg-amber-500/10 text-amber-700 border border-amber-500/20'
                  }`}>
                    <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium">{contentWarning.message}</p>
                      {contentWarning.categories && contentWarning.categories.length > 0 && (
                        <p className="text-xs mt-1">Categories: {contentWarning.categories.join(', ')}</p>
                      )}
                    </div>
                  </div>
                )}
                
                {moduleContentError && (
                  <div className="flex items-start gap-1.5 text-destructive text-xs">
                    <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{moduleContentError}</span>
                  </div>
                )}
              </div>
            )}

            {/* Text Content Form */}
            {moduleForm.type === 'text' && (
              <div className="space-y-2">
                <Label>{t('courses.textContent', 'Text Content')} *</Label>
                <Textarea
                  value={moduleForm.content}
                  onChange={(e) => {
                    setModuleForm(prev => ({ ...prev, content: e.target.value }));
                    if (e.target.value.trim().length >= 10) {
                      setModuleContentError('');
                    }
                  }}
                  placeholder={t('courses.textContentPlaceholder', 'Enter text content (minimum 10 characters)...')}
                  rows={6}
                  className={moduleContentError ? 'border-destructive' : ''}
                  data-testid="wizard-module-content"
                />
                {moduleContentError && (
                  <div className="flex items-start gap-1.5 text-destructive text-xs">
                    <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{moduleContentError}</span>
                  </div>
                )}
              </div>
            )}

            {/* Link Content Form */}
            {moduleForm.type === 'link' && (
              <div className="space-y-2">
                <Label>{t('courses.externalLink', 'External Link')} *</Label>
                <Input
                  value={moduleForm.content}
                  onChange={(e) => {
                    setModuleForm(prev => ({ ...prev, content: e.target.value }));
                    if (/^https?:\/\/.+/i.test(e.target.value.trim())) {
                      setModuleContentError('');
                    }
                  }}
                  placeholder="https://example.com/resource"
                  className={moduleContentError ? 'border-destructive' : ''}
                  data-testid="wizard-module-link"
                />
                {moduleContentError && (
                  <div className="flex items-start gap-1.5 text-destructive text-xs">
                    <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{moduleContentError}</span>
                  </div>
                )}
                <p className="text-xs text-muted-foreground">
                  {t('courses.linkHint', 'Provide a valid URL starting with http:// or https://')}
                </p>
              </div>
            )}

            {/* PDF Upload Form */}
            {moduleForm.type === 'pdf' && (
              <div className="space-y-3">
                <Label>{t('courses.pdfDocument', 'PDF Document')} *</Label>
                <div className="border-2 border-dashed rounded-lg p-4 text-center">
                  {uploadingDocument ? (
                    <div className="space-y-2">
                      <Loader2 className="w-6 h-6 mx-auto animate-spin text-primary" />
                      <p className="text-sm text-muted-foreground">{t('courses.uploading', 'Uploading...')}</p>
                    </div>
                  ) : moduleForm.content && moduleForm.content.includes('/uploads/documents/') ? (
                    <div className="space-y-2">
                      <CheckCircle2 className="w-6 h-6 mx-auto text-green-500" />
                      <p className="text-sm text-green-600">{t('courses.pdfUploaded', 'PDF uploaded successfully')}</p>
                      {moduleForm.original_filename && (
                        <p className="text-xs text-muted-foreground">{moduleForm.original_filename}</p>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setModuleForm(prev => ({ ...prev, content: '', original_filename: '' }))}
                      >
                        {t('courses.removeFile', 'Remove & Upload New')}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <FileType className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => documentInputRef.current?.click()}
                      >
                        {t('courses.uploadPdf', 'Upload PDF')}
                      </Button>
                      <p className="text-xs text-muted-foreground mt-2">
                        {t('courses.pdfHint', 'PDF files only (Max 50MB). Will be displayed in embedded viewer.')}
                      </p>
                      <input
                        type="file"
                        accept=".pdf,application/pdf"
                        className="hidden"
                        ref={documentInputRef}
                        onChange={(e) => handleDocumentUpload(e, 'pdf')}
                      />
                    </>
                  )}
                </div>
                {moduleContentError && (
                  <div className="flex items-start gap-1.5 text-destructive text-xs">
                    <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{moduleContentError}</span>
                  </div>
                )}
              </div>
            )}

            {/* PPT Upload Form */}
            {moduleForm.type === 'ppt' && (
              <div className="space-y-3">
                <Label>{t('courses.pptDocument', 'PowerPoint Presentation')} *</Label>
                <div className="border-2 border-dashed rounded-lg p-4 text-center">
                  {uploadingDocument ? (
                    <div className="space-y-2">
                      <Loader2 className="w-6 h-6 mx-auto animate-spin text-primary" />
                      <p className="text-sm text-muted-foreground">{t('courses.uploading', 'Uploading...')}</p>
                    </div>
                  ) : moduleForm.content && moduleForm.content.includes('/uploads/documents/') ? (
                    <div className="space-y-2">
                      <CheckCircle2 className="w-6 h-6 mx-auto text-green-500" />
                      <p className="text-sm text-green-600">{t('courses.pptUploaded', 'PowerPoint uploaded successfully')}</p>
                      {moduleForm.original_filename && (
                        <p className="text-xs text-muted-foreground">{moduleForm.original_filename}</p>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setModuleForm(prev => ({ ...prev, content: '', original_filename: '' }))}
                      >
                        {t('courses.removeFile', 'Remove & Upload New')}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <FileSpreadsheet className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => documentInputRef.current?.click()}
                      >
                        {t('courses.uploadPpt', 'Upload PowerPoint')}
                      </Button>
                      <p className="text-xs text-muted-foreground mt-2">
                        {t('courses.pptHint', 'PPT/PPTX files (Max 50MB). Will be available for download/view.')}
                      </p>
                      <input
                        type="file"
                        accept=".ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                        className="hidden"
                        ref={documentInputRef}
                        onChange={(e) => handleDocumentUpload(e, 'ppt')}
                      />
                    </>
                  )}
                </div>
                {moduleContentError && (
                  <div className="flex items-start gap-1.5 text-destructive text-xs">
                    <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{moduleContentError}</span>
                  </div>
                )}
              </div>
            )}

            {/* Audio Upload Form */}
            {moduleForm.type === 'audio' && (
              <div className="space-y-3">
                <Label>{t('courses.audioFile', 'Audio File')} *</Label>
                <div className="border-2 border-dashed rounded-lg p-4 text-center">
                  {uploadingAudio ? (
                    <div className="space-y-2">
                      <Loader2 className="w-6 h-6 mx-auto animate-spin text-primary" />
                      <p className="text-sm text-muted-foreground">{t('courses.uploading', 'Uploading...')}</p>
                    </div>
                  ) : moduleForm.content && moduleForm.content.includes('/uploads/audio/') ? (
                    <div className="space-y-2">
                      <CheckCircle2 className="w-6 h-6 mx-auto text-green-500" />
                      <p className="text-sm text-green-600">{t('courses.audioUploaded', 'Audio uploaded successfully')}</p>
                      {moduleForm.original_filename && (
                        <p className="text-xs text-muted-foreground">{moduleForm.original_filename}</p>
                      )}
                      {/* Audio preview */}
                      <audio controls className="w-full mt-2">
                        <source src={moduleForm.content} />
                        Your browser does not support the audio element.
                      </audio>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setModuleForm(prev => ({ ...prev, content: '', original_filename: '' }))}
                      >
                        {t('courses.removeFile', 'Remove & Upload New')}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Headphones className="w-6 h-6 mx-auto text-muted-foreground mb-2" />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => audioInputRef.current?.click()}
                      >
                        {t('courses.uploadAudio', 'Upload Audio')}
                      </Button>
                      <p className="text-xs text-muted-foreground mt-2">
                        {t('courses.audioHint', 'MP3, WAV, OGG, AAC, M4A (Max 50MB). Will be playable inline.')}
                      </p>
                      <input
                        type="file"
                        accept=".mp3,.wav,.ogg,.aac,.m4a,audio/*"
                        className="hidden"
                        ref={audioInputRef}
                        onChange={handleAudioUpload}
                      />
                    </>
                  )}
                </div>
                {moduleContentError && (
                  <div className="flex items-start gap-1.5 text-destructive text-xs">
                    <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span>{moduleContentError}</span>
                  </div>
                )}
              </div>
            )}

            {/* Slide Designer Form */}
            {moduleForm.type === 'slide' && (
              <div className="space-y-3">
                <Label>{t('courses.slideContent', 'Slide Content')}</Label>
                <div className="border rounded-lg p-4 bg-muted/20 text-center space-y-2">
                  {moduleForm.content ? (
                    <div className="space-y-2">
                      <CheckCircle2 className="w-6 h-6 mx-auto text-green-500" />
                      <p className="text-sm text-muted-foreground">
                        {(() => { 
                          try { 
                            const d = JSON.parse(moduleForm.content); 
                            return `${d.slides?.length || 0} slide(s) designed`; 
                          } catch { 
                            return 'Slide data saved'; 
                          } 
                        })()}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">{t('courses.noSlidesYet', 'No slides designed yet')}</p>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowModuleFormModal(false);
                      setSlideEditorOpen(true);
                    }}
                    data-testid="open-slide-designer"
                  >
                    <Presentation className="w-4 h-4 mr-2" />
                    {moduleForm.content ? t('courses.editSlides', 'Edit Slides') : t('courses.openSlideDesigner', 'Open Slide Designer')}
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={handleCloseModuleForm}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button onClick={handleAddModule} data-testid="save-module-btn">
              {editingModuleIndex !== null ? (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  {t('courses.updateModule', 'Update Module')}
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('courses.addModule', 'Add Module')}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );

  // Step 3: Quiz
  const renderQuizStep = () => {
    const currentQuizForm = getCurrentQuizForm();
    
    return (
    <div className="space-y-4 py-4">
      {/* Enable Quiz Toggle */}
      <div className="flex items-center justify-between p-3 rounded-lg border">
        <div>
          <Label>{t('courses.enableQuiz', 'Enable Quiz')}</Label>
          <p className="text-xs text-muted-foreground">
            {t('courses.enableQuizDesc', 'Add a quiz to test learners understanding')}
          </p>
        </div>
        <Switch
          checked={quizEnabled}
          onCheckedChange={(checked) => {
            setQuizEnabled(checked);
            if (!checked) {
              setSelectedQuizTypes([]);
              setActiveQuizType(null);
            }
          }}
          data-testid="wizard-quiz-enable"
        />
      </div>

      {quizEnabled && !activeQuizType && (
        <>
          <Separator />
          
          {/* Quiz Type Selection */}
          <div className="space-y-3">
            <Label className="text-base font-medium">Select Quiz Type(s)</Label>
            <p className="text-sm text-muted-foreground">Choose where you want to add quizzes in your course</p>
            
            <div className="space-y-2">
              {QUIZ_TYPE_OPTIONS.map((option) => {
                // Calculate questions count based on type
                let questionsCount = 0;
                if (option.value === 'in_between') {
                  questionsCount = getInBetweenQuestionsCount();
                } else {
                  questionsCount = quizForms[option.value]?.questions?.length || 0;
                }
                
                return (
                <div
                  key={option.value}
                  className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-colors ${
                    selectedQuizTypes.includes(option.value)
                      ? 'border-primary bg-primary/5'
                      : 'hover:border-muted-foreground/50'
                  }`}
                  onClick={() => {
                    setSelectedQuizTypes(prev =>
                      prev.includes(option.value)
                        ? prev.filter(t => t !== option.value)
                        : [...prev, option.value]
                    );
                  }}
                  data-testid={`quiz-type-${option.value}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      selectedQuizTypes.includes(option.value)
                        ? 'border-primary bg-primary'
                        : 'border-muted-foreground/50'
                    }`}>
                      {selectedQuizTypes.includes(option.value) && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium">{option.label}</p>
                      <p className="text-xs text-muted-foreground">{option.description}</p>
                    </div>
                  </div>
                  {questionsCount > 0 && (
                    <Badge variant="secondary" className="bg-green-100 text-green-700">
                      {questionsCount} questions
                    </Badge>
                  )}
                </div>
              );})}
            </div>
          </div>

          {/* Quiz Type Progress */}
          {selectedQuizTypes.length > 0 && (
            <div className="space-y-3 pt-4">
              <Label className="text-sm font-medium">Configure Your Quizzes</Label>
              <div className="grid gap-2">
                {selectedQuizTypes.map((type) => {
                  const option = QUIZ_TYPE_OPTIONS.find(o => o.value === type);
                  
                  // Special handling for in_between
                  if (type === 'in_between') {
                    const configuredCount = getConfiguredModulesCount();
                    const totalQuestions = getInBetweenQuestionsCount();
                    return (
                      <Button
                        key={type}
                        variant={configuredCount > 0 ? "outline" : "default"}
                        className={`justify-between h-auto py-3 ${configuredCount > 0 ? 'border-green-500' : ''}`}
                        onClick={() => setActiveQuizType(type)}
                        data-testid={`configure-${type}`}
                      >
                        <div className="flex items-center gap-2">
                          {configuredCount > 0 ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          ) : (
                            <FileQuestion className="w-4 h-4" />
                          )}
                          <span>{option?.label}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          {configuredCount > 0 ? (
                            <span className="text-green-600">{configuredCount} modules, {totalQuestions} questions</span>
                          ) : (
                            <span className="text-muted-foreground">Not configured</span>
                          )}
                          <ChevronRight className="w-4 h-4" />
                        </div>
                      </Button>
                    );
                  }
                  
                  const form = quizForms[type];
                  const hasQuestions = form?.questions?.length > 0;
                  return (
                    <Button
                      key={type}
                      variant={hasQuestions ? "outline" : "default"}
                      className={`justify-between h-auto py-3 ${hasQuestions ? 'border-green-500' : ''}`}
                      onClick={() => setActiveQuizType(type)}
                      data-testid={`configure-${type}`}
                    >
                      <div className="flex items-center gap-2">
                        {hasQuestions ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        ) : (
                          <FileQuestion className="w-4 h-4" />
                        )}
                        <span>{option?.label}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        {hasQuestions ? (
                          <span className="text-green-600">{form.questions.length} questions</span>
                        ) : (
                          <span className="text-muted-foreground">Not configured</span>
                        )}
                        <ChevronRight className="w-4 h-4" />
                      </div>
                    </Button>
                  );
                })}
              </div>
              
              {/* Validation message */}
              {selectedQuizTypes.length > 0 && !isStep3Valid() && (
                <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Configure all selected quiz types to proceed, or deselect unused types.
                  </p>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Quiz Configuration for Active Type */}
      {quizEnabled && activeQuizType && (
        <>
          {/* Back to Quiz Type Selection */}
          <div className="flex items-center gap-2 pb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (activeQuizType === 'in_between' && selectedModuleForQuiz !== null) {
                  setSelectedModuleForQuiz(null);
                } else {
                  setActiveQuizType(null);
                }
              }}
              className="gap-1"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </Button>
            <Badge variant="outline" className="text-sm">
              {QUIZ_TYPE_OPTIONS.find(o => o.value === activeQuizType)?.label}
              {activeQuizType === 'in_between' && selectedModuleForQuiz !== null && (
                <span className="ml-1">→ After Module {selectedModuleForQuiz + 1}</span>
              )}
            </Badge>
          </div>
          
          <Separator />
          
          {/* Module Selection for In Between Quizzes */}
          {activeQuizType === 'in_between' && selectedModuleForQuiz === null && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-base font-medium">Select Module</Label>
                <p className="text-sm text-muted-foreground">
                  Choose after which module you want to add a quiz
                </p>
              </div>
              
              {modules.length === 0 ? (
                <div className="text-center py-8 border border-dashed rounded-lg">
                  <FileQuestion className="w-10 h-10 mx-auto mb-3 text-muted-foreground/50" />
                  <p className="text-muted-foreground">No modules added yet</p>
                  <p className="text-sm text-muted-foreground">Go back to Step 2 to add modules first</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {modules.map((module, index) => {
                    const moduleQuiz = quizForms.in_between.module_quizzes?.[index];
                    const hasQuestions = moduleQuiz?.questions?.length > 0;
                    return (
                      <Button
                        key={index}
                        variant={hasQuestions ? "outline" : "secondary"}
                        className={`w-full justify-between h-auto py-3 ${hasQuestions ? 'border-green-500' : ''}`}
                        onClick={() => setSelectedModuleForQuiz(index)}
                        data-testid={`select-module-${index}`}
                      >
                        <div className="flex items-center gap-2 text-left">
                          {hasQuestions ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                          ) : (
                            <FileQuestion className="w-4 h-4 flex-shrink-0" />
                          )}
                          <div>
                            <p className="font-medium">After Module {index + 1}</p>
                            <p className="text-xs text-muted-foreground line-clamp-1">{module.title}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          {hasQuestions ? (
                            <Badge variant="secondary" className="bg-green-100 text-green-700">
                              {moduleQuiz.questions.length} questions
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">No quiz</span>
                          )}
                          <ChevronRight className="w-4 h-4" />
                        </div>
                      </Button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          
          {/* Quiz Settings - Show for pre_test, post_test, or when module is selected for in_between */}
          {(activeQuizType !== 'in_between' || selectedModuleForQuiz !== null) && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label>{t('courses.quizTitle', 'Quiz Title')}</Label>
                  <Input
                    value={currentQuizForm.title}
                    onChange={(e) => updateCurrentQuizForm({ title: e.target.value })}
                    placeholder={t('courses.quizTitlePlaceholder', 'e.g., Final Assessment')}
                    data-testid="wizard-quiz-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('courses.passingScore', 'Passing Score (%)')}</Label>
                  <Input
                    type="number"
                    value={currentQuizForm.passing_score}
                    onChange={(e) => updateCurrentQuizForm({ passing_score: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) })}
                    min={0}
                    max={100}
                    data-testid="wizard-quiz-passing-score"
                  />
                  {currentQuizForm.passing_score === 0 && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      All users will pass regardless of score
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{t('courses.timeLimit', 'Time Limit (Minutes)')} *</Label>
                  <Input
                    type="number"
                    value={currentQuizForm.time_limit}
                    onChange={(e) => updateCurrentQuizForm({ time_limit: parseInt(e.target.value) || 1 })}
                    min={1}
                    className={currentQuizForm.time_limit <= 0 ? 'border-destructive' : ''}
                    data-testid="wizard-quiz-time-limit"
                  />
                  {currentQuizForm.time_limit <= 0 && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {t('courses.timeLimitError', 'Time limit must be greater than 0')}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{t('courses.maxAttempts', 'Max Attempts')} *</Label>
                  <Input
                    type="number"
                    value={currentQuizForm.max_attempts}
                    onChange={(e) => updateCurrentQuizForm({ max_attempts: parseInt(e.target.value) || 1 })}
                    min={1}
                    className={currentQuizForm.max_attempts <= 0 ? 'border-destructive' : ''}
                    data-testid="wizard-quiz-max-attempts"
                  />
                  {currentQuizForm.max_attempts <= 0 && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {t('courses.maxAttemptsError', 'Max attempts must be greater than 0')}
                    </p>
                  )}
                </div>
              </div>

          {/* Shuffle Settings */}
          <div className="grid grid-cols-2 gap-4 p-3 rounded-lg border bg-muted/30">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm">{t('courses.shuffleQuestions', 'Shuffle Questions')}</Label>
                <p className="text-xs text-muted-foreground">{t('courses.shuffleQuestionsDesc', 'Randomize question order')}</p>
              </div>
              <Switch
                checked={currentQuizForm.shuffle_questions}
                onCheckedChange={(checked) => updateCurrentQuizForm({ shuffle_questions: checked })}
                data-testid="wizard-quiz-shuffle-questions"
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm">{t('courses.shuffleOptions', 'Shuffle Options')}</Label>
                <p className="text-xs text-muted-foreground">{t('courses.shuffleOptionsDesc', 'Randomize answer options')}</p>
              </div>
              <Switch
                checked={currentQuizForm.shuffle_options}
                onCheckedChange={(checked) => updateCurrentQuizForm({ shuffle_options: checked })}
                data-testid="wizard-quiz-shuffle-options"
              />
            </div>
          </div>

          {/* AI Generate Button - Only show if AI features are enabled */}
          {canUseAIQuizGen && (
            <Button
              onClick={() => handleGenerateQuizForType(activeQuizType)}
              disabled={generatingQuiz || modules.length === 0}
              variant="outline"
              className="w-full"
              data-testid="wizard-generate-quiz-btn"
            >
              {generatingQuiz ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              {t('courses.generateWithAI', 'Generate with AI')}
            </Button>
          )}

          <Separator />

          {/* Questions Header with Add Button */}
          <div className="flex items-center justify-between">
            <Label className="text-base font-medium">
              {t('courses.questions', 'Questions')} ({currentQuizForm.questions?.length || 0})
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button size="sm" data-testid="wizard-add-question-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  {t('courses.addQuestion', 'Add Question')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-2" align="end">
                <div className="space-y-1">
                  {QUESTION_TYPES.map(type => (
                    <Button
                      key={type.value}
                      variant="ghost"
                      className="w-full justify-start text-sm"
                      onClick={() => handleAddQuestionForType(activeQuizType, type.value)}
                      data-testid={`add-question-${type.value}`}
                    >
                      {type.label}
                    </Button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Questions List */}
          <div className="space-y-4 max-h-[400px] overflow-y-auto">
            {currentQuizForm.questions?.length === 0 ? (
              <div className="text-center py-8 border border-dashed rounded-lg">
                <FileQuestion className="w-10 h-10 mx-auto mb-3 text-muted-foreground/50" />
                <p className="text-muted-foreground">{t('courses.noQuestionsYet', 'No questions added yet')}</p>
                <p className="text-sm text-muted-foreground">Click "Add Question" to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {currentQuizForm.questions.map((question, index) => (
                  <div key={index} className="p-4 rounded-lg border bg-background">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">Q{index + 1}</Badge>
                        <Badge variant="outline" className="text-xs capitalize">
                          {question.type?.replace('_', ' ')}
                        </Badge>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleRemoveQuestionForType(activeQuizType, index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    <Textarea
                      value={question.question}
                      onChange={(e) => handleUpdateQuestionForType(activeQuizType, index, { question: e.target.value })}
                      placeholder={t('courses.enterQuestion', 'Enter your question...')}
                      rows={2}
                      className="mb-3"
                    />

                    {/* Options based on question type */}
                    {(question.type === 'single_choice' || question.type === 'multiple_choice') && (
                      <div className="space-y-2">
                        {(question.options || ['', '', '', '']).map((option, optIndex) => (
                          <div key={optIndex} className="flex items-center gap-2">
                            {question.type === 'multiple_choice' ? (
                              <input
                                type="checkbox"
                                checked={Array.isArray(question.correct_answer) ? question.correct_answer.includes(optIndex) : question.correct_answer === optIndex}
                                onChange={(e) => {
                                  let newAnswer;
                                  if (Array.isArray(question.correct_answer)) {
                                    newAnswer = e.target.checked
                                      ? [...question.correct_answer, optIndex]
                                      : question.correct_answer.filter(i => i !== optIndex);
                                  } else {
                                    newAnswer = e.target.checked ? [optIndex] : [];
                                  }
                                  handleUpdateQuestionForType(activeQuizType, index, { correct_answer: newAnswer });
                                }}
                                className="w-4 h-4"
                              />
                            ) : (
                              <input
                                type="radio"
                                name={`correct_${activeQuizType}_${index}`}
                                checked={question.correct_answer === optIndex}
                                onChange={() => handleUpdateQuestionForType(activeQuizType, index, { correct_answer: optIndex })}
                                className="w-4 h-4"
                              />
                            )}
                            <Input
                              value={option}
                              onChange={(e) => {
                                const newOptions = [...(question.options || ['', '', '', ''])];
                                newOptions[optIndex] = e.target.value;
                                handleUpdateQuestionForType(activeQuizType, index, { options: newOptions });
                              }}
                              placeholder={`Option ${optIndex + 1}`}
                              className="flex-1"
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {question.type === 'true_false' && (
                      <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name={`tf_${activeQuizType}_${index}`}
                            checked={question.correct_answer === 0}
                            onChange={() => handleUpdateQuestionForType(activeQuizType, index, { correct_answer: 0 })}
                            className="w-4 h-4"
                          />
                          <span>True</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            name={`tf_${activeQuizType}_${index}`}
                            checked={question.correct_answer === 1}
                            onChange={() => handleUpdateQuestionForType(activeQuizType, index, { correct_answer: 1 })}
                            className="w-4 h-4"
                          />
                          <span>False</span>
                        </label>
                      </div>
                    )}

                    {question.type === 'match_following' && (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2 text-xs font-medium text-muted-foreground">
                          <span>Left Column</span>
                          <span>Right Column (Correct Match)</span>
                        </div>
                        {(question.pairs || [{ left: '', right: '' }, { left: '', right: '' }]).map((pair, pairIndex) => (
                          <div key={pairIndex} className="grid grid-cols-2 gap-2">
                            <Input
                              value={pair.left}
                              onChange={(e) => {
                                const newPairs = [...(question.pairs || [{ left: '', right: '' }, { left: '', right: '' }])];
                                newPairs[pairIndex].left = e.target.value;
                                handleUpdateQuestionForType(activeQuizType, index, { pairs: newPairs });
                              }}
                              placeholder={`Item ${pairIndex + 1}`}
                            />
                            <Input
                              value={pair.right}
                              onChange={(e) => {
                                const newPairs = [...(question.pairs || [{ left: '', right: '' }, { left: '', right: '' }])];
                                newPairs[pairIndex].right = e.target.value;
                                handleUpdateQuestionForType(activeQuizType, index, { pairs: newPairs });
                              }}
                              placeholder={`Match ${pairIndex + 1}`}
                            />
                          </div>
                        ))}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newPairs = [...(question.pairs || []), { left: '', right: '' }];
                            handleUpdateQuestionForType(activeQuizType, index, { pairs: newPairs });
                          }}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add Pair
                        </Button>
                      </div>
                    )}

                    <div className="mt-3">
                      <Input
                        value={question.explanation || ''}
                        onChange={(e) => handleUpdateQuestionForType(activeQuizType, index, { explanation: e.target.value })}
                        placeholder="Explanation (optional)"
                        className="text-sm"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
            </>
          )}
        </>
      )}
    </div>
  );
  };

  // Step 4: Assignment
  // Step 4: Assignment
  const renderAssignmentStep = () => (
    <div className="space-y-4 py-4">
      <div className="p-4 rounded-lg bg-muted/50">
        <p className="text-sm text-muted-foreground">
          {t('courses.assignmentOptional', 'Assignment is optional. You can skip this step and assign the course later from the course card.')}
        </p>
      </div>

      {/* Publish Course Toggle */}
      <div className="flex items-center justify-between p-3 rounded-lg border">
        <div>
          <Label>{t('courses.publishCourse', 'Publish Course')}</Label>
          <p className="text-xs text-muted-foreground">
            {t('courses.publishDescription', 'Make this course visible to learners')}
          </p>
        </div>
        <Switch
          checked={courseForm.is_published}
          onCheckedChange={(checked) => setCourseForm(prev => ({ ...prev, is_published: checked }))}
          data-testid="wizard-course-publish"
        />
      </div>

      <Separator />

      {/* Role Selection */}
      <div className="space-y-2">
        <Label>{t('courses.assignToRoles', 'Assign to Roles')}</Label>
        <Popover open={roleSelectOpen} onOpenChange={setRoleSelectOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              className="w-full justify-between"
              data-testid="wizard-role-select"
            >
              {assignmentForm.roles.length > 0
                ? `${assignmentForm.roles.length} ${t('courses.rolesSelected', 'roles selected')}`
                : t('courses.selectRoles', 'Select roles...')}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0">
            <Command>
              <CommandInput placeholder={t('courses.searchRoles', 'Search roles...')} />
              <CommandList>
                <CommandEmpty>{t('common.noResults', 'No results found.')}</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      if (assignmentForm.roles.length === availableRoles.length) {
                        setAssignmentForm(prev => ({ ...prev, roles: [] }));
                      } else {
                        setAssignmentForm(prev => ({ ...prev, roles: availableRoles.map(r => r.value) }));
                      }
                    }}
                  >
                    <div className={cn(
                      "mr-2 h-4 w-4 border rounded flex items-center justify-center",
                      assignmentForm.roles.length === availableRoles.length 
                        ? "bg-primary border-primary" 
                        : "border-muted-foreground"
                    )}>
                      {assignmentForm.roles.length === availableRoles.length && (
                        <Check className="h-3 w-3 text-primary-foreground" />
                      )}
                    </div>
                    {t('courses.selectAll', 'Select All')}
                  </CommandItem>
                  {availableRoles.map((role) => (
                    <CommandItem
                      key={role.value}
                      onSelect={() => {
                        setAssignmentForm(prev => ({
                          ...prev,
                          roles: prev.roles.includes(role.value)
                            ? prev.roles.filter(r => r !== role.value)
                            : [...prev.roles, role.value]
                        }));
                      }}
                    >
                      <div className={cn(
                        "mr-2 h-4 w-4 border rounded flex items-center justify-center",
                        assignmentForm.roles.includes(role.value) 
                          ? "bg-primary border-primary" 
                          : "border-muted-foreground"
                      )}>
                        {assignmentForm.roles.includes(role.value) && (
                          <Check className="h-3 w-3 text-primary-foreground" />
                        )}
                      </div>
                      {role.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Selected Roles Badges */}
        {assignmentForm.roles.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {assignmentForm.roles.map(role => {
              const roleInfo = availableRoles.find(r => r.value === role);
              return (
                <Badge key={role} variant="secondary" className="flex items-center gap-1">
                  {roleInfo?.label || role}
                  <X
                    className="w-3 h-3 cursor-pointer"
                    onClick={() => setAssignmentForm(prev => ({
                      ...prev,
                      roles: prev.roles.filter(r => r !== role)
                    }))}
                  />
                </Badge>
              );
            })}
          </div>
        )}
      </div>

      {/* Due Date */}
      {assignmentForm.roles.length > 0 && (
        <div className="space-y-2">
          <Label>{t('courses.dueDate', 'Due Date')} *</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !assignmentForm.due_date && "text-muted-foreground"
                )}
                data-testid="wizard-due-date"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {assignmentForm.due_date
                  ? format(assignmentForm.due_date, 'PPP')
                  : t('courses.selectDueDate', 'Select due date')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={assignmentForm.due_date}
                onSelect={(date) => setAssignmentForm(prev => ({ ...prev, due_date: date }))}
                disabled={(date) => isBefore(date, startOfDay(new Date()))}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      )}
    </div>
  );

  return (
    <>
    <Dialog open={open && !slideEditorOpen} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-2xl max-h-[90vh] flex flex-col [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="relative">
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-0 top-0 h-8 w-8 rounded-full z-10"
            onClick={() => setShowCloseConfirm(true)}
            data-testid="wizard-close-btn"
          >
            <X className="h-4 w-4" />
          </Button>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            {editingCourse 
              ? t('courses.editCourse', 'Edit Course') 
              : t('courses.createCourse', 'Create Course')}
          </DialogTitle>
          <DialogDescription>
            {t('courses.wizardDescription', 'Complete the steps below to create or edit your course')}
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="flex items-center justify-between px-2 py-4 border-b">
          {steps.map((step, index) => (
            <React.Fragment key={step.number}>
              <div
                className={cn(
                  "flex items-center gap-2 cursor-pointer",
                  currentStep === step.number && "text-primary",
                  currentStep > step.number && "text-green-500",
                  currentStep < step.number && "text-muted-foreground"
                )}
                onClick={() => {
                  if (step.number < currentStep) {
                    setCurrentStep(step.number);
                  }
                }}
              >
                <div
                  className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2",
                    currentStep === step.number && "border-primary bg-primary text-primary-foreground",
                    currentStep > step.number && "border-green-500 bg-green-500 text-white",
                    currentStep < step.number && "border-muted-foreground"
                  )}
                >
                  {currentStep > step.number ? (
                    <CheckCircle2 className="w-4 h-4" />
                  ) : (
                    step.number
                  )}
                </div>
                <div className="hidden sm:block">
                  <p className="text-sm font-medium">{step.title}</p>
                  {step.optional && (
                    <p className="text-xs text-muted-foreground">{t('common.optional', 'Optional')}</p>
                  )}
                </div>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={cn(
                    "flex-1 h-0.5 mx-2",
                    currentStep > step.number ? "bg-green-500" : "bg-muted"
                  )}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step Content */}
        <div className="flex-1 min-h-0 overflow-y-auto px-1">
          {renderStepContent()}
        </div>

        {/* Footer Navigation */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1 || saving}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            {t('common.back', 'Back')}
          </Button>

          <div className="flex items-center gap-2">
            {currentStep === 4 && (
              <Button
                variant="ghost"
                onClick={handleSkipAssignment}
                disabled={saving}
                data-testid="wizard-skip-btn"
              >
                <SkipForward className="w-4 h-4 mr-2" />
                {t('courses.skipAssignment', 'Skip & Finish')}
              </Button>
            )}
            
            {currentStep < 4 ? (
              <Button 
                onClick={handleNext} 
                disabled={saving || !isCurrentStepValid()} 
                data-testid="wizard-next-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                {t('common.next', 'Next')}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button onClick={handleFinish} disabled={saving} data-testid="wizard-finish-btn">
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                )}
                {t('courses.finishAndAssign', 'Finish & Assign')}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>

      {/* Close Confirmation Dialog */}
      <Dialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('courses.discardChanges', 'Discard Changes?')}</DialogTitle>
            <DialogDescription>
              {t('courses.discardChangesDesc', 'You have unsaved changes. Are you sure you want to close? All progress will be lost.')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowCloseConfirm(false)}>
              {t('courses.noKeepEditing', 'No, Keep Editing')}
            </Button>
            <Button 
              variant="destructive" 
              onClick={() => {
                setShowCloseConfirm(false);
                onOpenChange(false);
              }}
            >
              {t('courses.yesDiscard', 'Yes, Discard')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>

    {/* Slide Editor Full-Screen Overlay - Outside Dialog */}
    {slideEditorOpen && (
      <SlideEditor
        initialData={moduleForm.content}
        onSave={(data) => {
          setModuleForm(prev => ({ ...prev, content: data }));
          setSlideEditorOpen(false);
          setShowModuleFormModal(true);
        }}
        onClose={() => {
          setSlideEditorOpen(false);
          setShowModuleFormModal(true);
        }}
      />
    )}
    </>
  );
};

export default CourseWizard;
